<template>
  <BarLoader />
</template>

<script lang="ts">
import axios, { AxiosError } from "axios";
import { defineComponent, ref, getCurrentInstance, watch } from "vue";
import { ErrorResponse } from "@/types/interfaces";
import { useUserStore } from "@/stores/user";
import { useRouter } from "vue-router";
import { PostHog } from "posthog-js";
import { useFrontegg } from "@frontegg/vue";
import BarLoader from "@/components/shared/BarLoader.vue";
import { User } from "@/types/user-types";
import { Company } from "@/types/company-types";
export default defineComponent({
  name: "HomeView",
  components: {
    BarLoader,
  },
  setup() {
    const email = ref("");
    const errorMessage = ref<string | null>(null);
    const userStore = useUserStore();
    const router = useRouter();
    const posthog = (getCurrentInstance()?.proxy as { $posthog: PostHog })
      ?.$posthog;
    const { authState, loginWithRedirect } = useFrontegg();

    const handleExpertUser = () => {
      window.open(process.env.VUE_APP_EXPERT_APP_BASE_URL, "_self");
    };

    const getSignInRequestParams = (email: string, token: string) => {
      return {
        url: process.env.VUE_APP_SIGN_IN_URL,
        data: {
          user: {
            email: email,
            token: token,
            metadata: authState.user?.metadata,
          },
        },
        config: {
          headers: {
            "Content-Type": "application/json",
          },
        },
      };
    };

    const handleAuthSuccess = (
      token: string,
      user: User,
      company: Company,
      hasCompany: boolean,
      personalInfoRequired: boolean,
      addSubscription: boolean,
      customPayoutFee: number | null
    ) => {
      setAuthStore(user, company, token, addSubscription, customPayoutFee);
      produktlyIdentifyUser(user.uuid, email.value);
      notifyPosthog(user.uuid, personalInfoRequired);
      postAuthRedirection(hasCompany, personalInfoRequired);
    };

    const postAuthRedirection = (
      hasCompany: boolean,
      personalInfoRequired: boolean
    ) => {
      if (!hasCompany || personalInfoRequired) {
        const pendingCompanyDetails = !hasCompany;

        router.push({
          name: "AddCompanyDetails",
          query: { pendingCompanyDetails: pendingCompanyDetails.toString() },
        });
      } else {
        router.push({ name: "SignInLanding" });
      }
    };

    const setAuthStore = (
      user: User,
      company: Company,
      token: string,
      addSubscription: boolean,
      customPayoutFee: number | null
    ) => {
      if (email.value !== user.firstName) {
        userStore.setFirstName(user.firstName);
      }
      userStore.setLastName(user.lastName);
      userStore.setTitle(user.title);
      userStore.setToken(token);
      userStore.setEmail(email.value);
      userStore.setUserType("company_user");
      userStore.setSwitchable(user.switchable);
      userStore.setCompanyName(company.name);
      userStore.setCompanyWebsite(company.website);
      userStore.setAddSubscription(addSubscription);
      userStore.setCustomPayoutFee(customPayoutFee);
    };

    const produktlyIdentifyUser = (uuid: string, email: string) => {
      if (window.Produktly) {
        window.Produktly.identifyUser(uuid, {
          email: email,
        });
      }
    };

    const notifyPosthog = (uuid: string, personalInfoRequired: boolean) => {
      if (posthog) {
        if (personalInfoRequired) {
          posthog.capture("Company_User_Clicked_On_Invite_Signup", {
            email: email.value,
          });
        } else {
          posthog.capture("Company_User_Clicked_Signup", {
            email: email.value,
          });
        }

        posthog.identify(uuid, {
          email: email.value,
          company_email: email.value,
          expert_user: false,
          company_user: true,
          attendee_user: false,
          company_first: userStore.firstName,
          company_last: userStore.lastName,
          company_name: userStore.companyName,
          company_website: userStore.companyWebsite,
        });
      }
    };

    const handleErrorResponse = (error: AxiosError<ErrorResponse>) => {
      if (error.response) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          return errorData;
        } else if (Array.isArray(errorData.errors)) {
          return errorData.errors.join(", ");
        } else if (typeof errorData.errors === "string") {
          return errorData.errors;
        } else if (typeof errorData.error === "string") {
          return errorData.error;
        }
      }
      return "An unexpected error occurred.";
    };

    const login = () => {
      loginWithRedirect();
    };

    watch(
      () => authState.user,
      (newValue) => {
        if (newValue) {
          email.value = newValue?.email;
          const { url, data, config } = getSignInRequestParams(
            newValue?.email,
            newValue?.accessToken
          );
          axios
            .post(url, data, config)
            .then((response) => {
              userStore.clearStore();
              if (
                (response.data.signup_as &&
                  response.data.signup_as == "expert_user") ||
                (response.data.login_as &&
                  response.data.login_as == "expert_user")
              ) {
                handleExpertUser();
              } else {
                handleAuthSuccess(
                  response.data.token,
                  response.data.user,
                  response.data.company,
                  response.data.has_company,
                  response.data.personal_info_required,
                  response.data.add_subscription,
                  response.data.custom_payout_fee
                );
              }
            })
            .catch((error) => {
              errorMessage.value = handleErrorResponse(
                error as AxiosError<ErrorResponse>
              );
              login();
            });
        }
      },
      { immediate: true }
    );

    return {
      email,
      errorMessage,
      authState,
      login,
    };
  },
});
</script>
